import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductionPlanPreview, ActiveRequest, RequestDraft } from "./plan-import-types";
import { procurementActionLabel, canRequestMaterial } from "../production-material-procurement";
import { formatQuantity } from "./plan-import-presentation";
import { requestCategory, requestPriority, requestQuantity } from "./plan-request-rules";
import { planImportGateway } from "./plan-import-gateway";
import type { PlanImportSharedState } from "./plan-import-workspace-contract";
export function usePlanRequestWorkspace({ preview, selectedPlanId, setPreview, setActiveTab, setPage, setError, today, currentAssessment, notify }: Pick<PlanImportSharedState, "preview" | "selectedPlanId" | "setPreview" | "setActiveTab" | "setPage" | "setError"> & {
    today: string;
    currentAssessment: boolean;
    notify?: (message: string) => void;
}) {
    const [selectedMaterialCodes, setSelectedMaterialCodes] = useState<Set<string>>(new Set());
    const [activeRequests, setActiveRequests] = useState<Record<string, ActiveRequest>>({});
    const [activeRequestsReady, setActiveRequestsReady] = useState(false);
    const [activeRequestError, setActiveRequestError] = useState("");
    const activeRequestRunRef = useRef(0);
    const [requestReviewOpen, setRequestReviewOpen] = useState(false);
    const [requestDrafts, setRequestDrafts] = useState<RequestDraft[]>([]);
    const [requestReviewStep, setRequestReviewStep] = useState<"EDIT" | "REVIEW">("EDIT");
    const [batchIdempotencyKey, setBatchIdempotencyKey] = useState("");
    const [creatingRequests, setCreatingRequests] = useState(false);
    const [overdueOverrideAcknowledged, setOverdueOverrideAcknowledged] = useState(false);
    const [overdueOverrideReason, setOverdueOverrideReason] = useState("");
    const applyPreview = useCallback((result: ProductionPlanPreview) => {
        setPreview(result);
        setSelectedMaterialCodes(new Set());
        setRequestReviewOpen(false);
        setRequestDrafts([]);
        setRequestReviewStep("EDIT");
        setBatchIdempotencyKey("");
        setOverdueOverrideAcknowledged(false);
        setOverdueOverrideReason("");
        setActiveRequests({});
        setActiveRequestsReady(false);
        setActiveRequestError("");
        setActiveTab(result.materialAssessment === "NOT_REQUESTED" ? "PLAN" : "SHORTAGES");
        setPage(1);
        const run = ++activeRequestRunRef.current;
        if (result.materialAssessment === "NOT_REQUESTED") {
            setActiveRequestsReady(true);
            return;
        }
        const codes = result.materials.map((item) => item.materialCode);
        const chunks = Array.from({ length: Math.ceil(codes.length / 200) }, (_, index) => codes.slice(index * 200, (index + 1) * 200));
        void Promise.all(chunks.map((materialCodes) => planImportGateway.activeRequests(materialCodes))).then((responses) => {
            if (activeRequestRunRef.current !== run)
                return;
            const items = responses.flatMap((response) => response.items);
            setActiveRequests(Object.fromEntries(items.map((item) => [item.materialCode, item])));
            setActiveRequestsReady(true);
            setSelectedMaterialCodes((current) => new Set([...current].filter((code) => !items.some((item) => item.materialCode === code))));
        }).catch(() => {
            if (activeRequestRunRef.current !== run)
                return;
            setActiveRequestError("Mevcut satın alma talepleri kontrol edilemedi. Yeni talep açmadan önce planı tekrar açın.");
        });
    }, [setPreview, setActiveTab, setPage]);
    const toggleMaterial = (materialCode: string) => {
        setSelectedMaterialCodes((current) => {
            const next = new Set(current);
            if (next.has(materialCode))
                next.delete(materialCode);
            else
                next.add(materialCode);
            return next;
        });
        setRequestReviewOpen(false);
    };
    const openRequestReview = () => {
        if (!preview || !activeRequestsReady)
            return;
        setRequestDrafts(preview.materials
            .filter((material) => selectedMaterialCodes.has(material.materialCode) && canRequestMaterial(material, currentAssessment))
            .map((material) => {
            const quantity = requestQuantity(material.purchaseQuantity ?? 0, material.unit);
            return { materialCode: material.materialCode, quantity, originalQuantity: quantity, due: material.firstRequiredAt < today ? today : material.firstRequiredAt };
        }));
        setRequestReviewStep("EDIT");
        setOverdueOverrideAcknowledged(false);
        setOverdueOverrideReason("");
        setBatchIdempotencyKey(crypto.randomUUID());
        setRequestReviewOpen(true);
    };
    const openSingleRequestReview = (materialCode: string) => {
        if (!preview || !activeRequestsReady || activeRequests[materialCode])
            return;
        const material = preview.materials.find((item) => item.materialCode === materialCode);
        if (!material || !canRequestMaterial(material, currentAssessment))
            return;
        setSelectedMaterialCodes(new Set([materialCode]));
        const quantity = requestQuantity(material.purchaseQuantity ?? 0, material.unit);
        setRequestDrafts([{ materialCode, quantity, originalQuantity: quantity, due: material.firstRequiredAt < today ? today : material.firstRequiredAt }]);
        setRequestReviewStep("EDIT");
        setOverdueOverrideAcknowledged(false);
        setOverdueOverrideReason("");
        setBatchIdempotencyKey(crypto.randomUUID());
        setRequestReviewOpen(true);
    };
    const requestBatchLines = () => {
        if (!preview || !activeRequestsReady)
            return [];
        return requestDrafts.flatMap((draft) => {
            const material = preview.materials.find((item) => item.materialCode === draft.materialCode);
            if (!material || !canRequestMaterial(material, currentAssessment) || draft.quantity <= 0 || !draft.due)
                return [];
            return [{
                    materialCode: material.materialCode,
                    materialName: material.materialName || material.materialCode,
                    description: [
                        `Üretim planı kaynaklı satın alma ihtiyacı. Malzeme kodu: ${material.materialCode}.`,
                        `Plan: ${preview.sheetName}, kontrol ${preview.fileFingerprint}.`,
                        `Hesap zamanı: ${preview.refreshedAt}. Karar: ${procurementActionLabel(material)}. Doğrulanan yeni satın alma: ${formatQuantity(material.purchaseQuantity ?? 0, material.unit)}.`,
                        `Kaynak Mikro iş emirleri: ${[...new Set((material.sourceWorkOrders ?? []).map((item) => item.workOrderNo))].join(", ")}.`,
                        `Plan tüketimi ${formatQuantity(material.totalRequiredQuantity, material.unit)}, mevcut stok ${formatQuantity(material.onHandQuantity, material.unit)}, açık sipariş bakiyesi ${formatQuantity(material.inboundQuantity, material.unit)}.`,
                        (material.overdueInboundQuantity ?? 0) > 0
                            ? `Gecikmiş açık sipariş ${formatQuantity(material.overdueInboundQuantity ?? 0, material.unit)}; kullanıcı kontrolü: ${overdueOverrideReason.trim()}.`
                            : "",
                    ].filter(Boolean).join(" "),
                    category: requestCategory(material.materialCode),
                    quantity: draft.quantity,
                    unit: material.unit.toLocaleLowerCase("tr-TR") || "adet",
                    priority: requestPriority(draft.due),
                    department: "Planlama",
                    due: draft.due,
                    proofRefreshedAt: preview.refreshedAt,
                    proofSourceWorkOrders: [...new Set((material.sourceWorkOrders ?? []).map((item) => item.workOrderNo))],
                }];
        });
    };
    const reviewSelectedRequests = () => {
        if (requestDrafts.length === 0 || requestBatchLines().length !== requestDrafts.length)
            return;
        if (overdueDraftMaterials.length > 0 && (!overdueOverrideAcknowledged || overdueOverrideReason.trim().length < 10)) {
            setError("Gecikmiş sipariş bulunan kalemler için kontrol onayı ve en az 10 karakterlik gerekçe girin.");
            return;
        }
        setError("");
        setRequestReviewStep("REVIEW");
    };
    const saveRequestBatch = async (submit: boolean) => {
        if (!preview || requestDrafts.length === 0)
            return;
        setCreatingRequests(true);
        setError("");
        try {
            const lines = requestBatchLines();
            if (lines.length !== requestDrafts.length)
                throw new Error("Talep miktarı ve ihtiyaç tarihlerini kontrol edin.");
            let batch = await planImportGateway.saveBatch(batchIdempotencyKey || crypto.randomUUID(), {
                planId: preview.savedPlan?.id ?? (selectedPlanId || undefined),
                weekStart: preview.weekStart,
                refreshedAt: preview.refreshedAt,
                lines,
            });
            if (submit) {
                batch = await planImportGateway.submitBatch(batch.id);
            }
            setActiveRequests((current) => ({
                ...current,
                ...Object.fromEntries(batch.requests.map((item) => [item.materialCode, {
                        materialCode: item.materialCode,
                        requestNo: item.requestNo,
                        status: submit ? "SUBMITTED" : "DRAFT",
                    }])),
            }));
            setSelectedMaterialCodes(new Set());
            setRequestReviewOpen(false);
            notify?.(submit
                ? `${batch.requests.length} talep onaya gönderildi. Grup: ${batch.batch_no}`
                : `${batch.requests.length} talep taslak kaydedildi. Grup: ${batch.batch_no}`);
        }
        catch (requestError) {
            const message = requestError instanceof Error ? requestError.message : "Talep grubu kaydedilemedi.";
            setError(message);
            notify?.(message);
        }
        finally {
            setCreatingRequests(false);
        }
    };
    const changedRequestCount = requestDrafts.filter((item) => item.quantity !== item.originalQuantity).length;
    const rawMaterialRequestCount = requestDrafts.filter((item) => requestCategory(item.materialCode) === "Hammadde").length;
    const packagingRequestCount = requestDrafts.length - rawMaterialRequestCount;
    const criticalRequestCount = requestDrafts.filter((item) => requestPriority(item.due) === "CRITICAL").length;
    const overdueDraftMaterials = requestDrafts.flatMap((draft) => {
        const material = preview?.materials.find((item) => item.materialCode === draft.materialCode);
        return material && (material.overdueInboundQuantity ?? 0) > 0 ? [material] : [];
    });
    const requestDraftInvalid = requestDrafts.some((item) => {
        const material = preview?.materials.find((candidate) => candidate.materialCode === item.materialCode);
        return !material || !canRequestMaterial(material, currentAssessment) || !Number.isFinite(item.quantity) || item.quantity <= 0 || item.quantity > requestQuantity(material.purchaseQuantity ?? 0, material.unit) || !item.due || item.due < today;
    }) || !activeRequestsReady ||
        (overdueDraftMaterials.length > 0 && (!overdueOverrideAcknowledged || overdueOverrideReason.trim().length < 10));
    return { selectedMaterialCodes, setSelectedMaterialCodes, activeRequests, activeRequestsReady, activeRequestError, requestReviewOpen, setRequestReviewOpen, requestDrafts, setRequestDrafts, requestReviewStep, setRequestReviewStep, creatingRequests, overdueOverrideAcknowledged, setOverdueOverrideAcknowledged, overdueOverrideReason, setOverdueOverrideReason, toggleMaterial, openRequestReview, openSingleRequestReview, reviewSelectedRequests, saveRequestBatch, changedRequestCount, rawMaterialRequestCount, packagingRequestCount, criticalRequestCount, overdueDraftMaterials, requestDraftInvalid, applyPreview, setActiveRequests };
}
export function usePlanRequestDrawerEffect({ creatingRequests, requestReviewOpen, setRequestReviewOpen }: Pick<ReturnType<typeof usePlanRequestWorkspace>, "creatingRequests" | "requestReviewOpen" | "setRequestReviewOpen">) {
    useEffect(() => {
        if (!requestReviewOpen)
            return;
        document.body.classList.add("production-plan-request-drawer-open");
        document.documentElement.classList.add("production-plan-request-drawer-open");
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !creatingRequests)
                setRequestReviewOpen(false);
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.body.classList.remove("production-plan-request-drawer-open");
            document.documentElement.classList.remove("production-plan-request-drawer-open");
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [creatingRequests, requestReviewOpen]);
}
